import { Typography } from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { ColorDynamic, Column, Columns, Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { startIntercomTour } from 'shared/helpers/Intercom';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { ArrowRightRoundedIcon } from 'shared/icons/ArrowRightRounded';
import { ChatIcon } from 'shared/icons/ChatIcon';
import { LoadsSidebarIcon } from 'shared/icons/LoadsSidebarIcon';
import { SuperCardIcon } from 'shared/icons/SuperCardIcon';
import { VerificationIcon } from 'shared/icons/VerificationIcon';
import { becomeVerifiedCarrier } from 'shared/modules/loadboard/core/BecomVerifiedCarrier';
import {
  CarrierSettings,
  useCarrierSettings,
} from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import { GET_TO_KNOW_CARRIER_TMS_TOUR_ID } from '../data/OnboardingConstants';
import { OnboardingStep } from '../data/OnboardingDTO';
import { useSaveOnboardingWithErrorHandler } from '../data/useSaveOnboardingWithErrorHandler';
import { OnboardingCompletion } from './OnboardingCompletion';
import { OnboardingTaskItem } from './OnboardingTaskItem';

const ArrowBox = styled(Box)`
  width: 70px;
  display: flex;
  justify-content: flex-end;
`;

interface OnboardingTasksProps {
  onClose: () => void;
  completed: boolean;
  onboardingSteps?: OnboardingStep;
}

export function OnboardingTasks({
  onClose,
  completed,
  onboardingSteps,
}: OnboardingTasksProps) {
  if (completed) {
    return <OnboardingCompletion />;
  }

  return (
    <NextExploreSteps onboardingSteps={onboardingSteps} onClose={onClose} />
  );
}

interface NextExploreStepsProps {
  onboardingSteps?: OnboardingStep;
  onClose: () => void;
}

function NextExploreSteps({ onboardingSteps, onClose }: NextExploreStepsProps) {
  const steps = onboardingSteps?.steps;
  const { data: settings } = useCarrierSettings();

  const { mutateAsync: saveStep, isPending } =
    useSaveOnboardingWithErrorHandler();
  const isSuperCardEnabled = useFlag('supercard_management');

  const isCreateOrImportLoadCompleted =
    steps?.tour_create_or_import_load?.status === 'started';
  const isGetToKnowCarrierTMSCompleted =
    steps?.get_to_know_ctms?.status === 'started';
  const isSLBSearchTourCompleted = steps?.tour_slb_search?.status === 'started';
  const exploreSuperCardStep = steps?.explore_supercard;
  const shouldShowExploreSuperCard =
    isSuperCardEnabled && exploreSuperCardStep?.status === 'pending';
  const shouldShowVerificationCard =
    settings?.carrier?.verification_status === 'not_submitted';

  return (
    <Columns align="top" space="small">
      <Column width="content">
        <ArrowBox>
          <ArrowRightRoundedIcon />
        </ArrowBox>
      </Column>
      <Column width="fluid">
        <Stack space="large">
          <Stack space="small">
            <Stack space="xsmall">
              <Typography variant="h3">Next, explore Carrier TMS</Typography>
              {shouldShowGettingStartedIntro(
                settings?.carrier?.verification_status,
              ) && (
                <Typography color="textSecondary">
                  While waiting for verification, explore Carrier TMS to prepare
                  for requesting loads once you&apos;re approved. You can also
                  create or import a load now, and set your drivers up with a
                  fuel card.
                </Typography>
              )}
            </Stack>

            {!isSLBSearchTourCompleted && (
              <OnboardingTaskItem
                name="Explore Super Loadboard"
                description="Access loads now! More loads unlock once you're verified."
                icon={<Search htmlColor={ColorDynamic.Dark100} />}
                onClick={() => {
                  void saveStep({
                    status: 'started',
                    stepName: 'tour_slb_search',
                  }).then(() => {
                    openExternalURL('/loadboard?launch_slb_search_tour=true');
                  });
                  onClose();
                }}
              />
            )}
            {shouldShowVerificationCard && (
              <OnboardingTaskItem
                name="Become Verified Carrier"
                description="Unlock unlimited and free load access. Apply now and start booking."
                icon={<VerificationIcon />}
                onClick={() => {
                  if (MobileAppBridge.isInjected()) {
                    MobileAppBridge.openVerifiedCarrierPromotionalModal();
                  } else {
                    becomeVerifiedCarrier(
                      settings?.carrier?.verification_status,
                      { source: 'onboarding_dialog' },
                    );
                  }
                  onClose();
                }}
              />
            )}

            {!isCreateOrImportLoadCompleted && (
              <OnboardingTaskItem
                name="Create or Import Load"
                description="Manage your loads with Carrier TMS and streamline your operations."
                icon={<LoadsSidebarIcon htmlColor={ColorDynamic.Dark100} />}
                onClick={() => {
                  void saveStep({
                    status: 'started',
                    stepName: 'tour_create_or_import_load',
                  }).then(() => {
                    openExternalURL(
                      '/tms/loads?launch_create_import_loads_tour=true',
                    );
                  });
                  onClose();
                }}
              />
            )}
            {!isGetToKnowCarrierTMSCompleted && (
              <OnboardingTaskItem
                name="Get to Know Carrier TMS"
                description="Take a quick tour to master Carrier TMS."
                icon={<ChatIcon />}
                onClick={() => {
                  startIntercomTour(GET_TO_KNOW_CARRIER_TMS_TOUR_ID);
                  void saveStep({
                    status: 'started',
                    stepName: 'get_to_know_ctms',
                  });
                  onClose();
                }}
              />
            )}
            {shouldShowExploreSuperCard && (
              <OnboardingTaskItem
                name="Explore SuperCard"
                description={
                  <>
                    Give your drivers a fuel card that saves up to $2/gal.
                    <br />
                    No hidden fees. Integrated to driver app.
                  </>
                }
                icon={<SuperCardIcon htmlColor={ColorDynamic.Dark100} />}
                onClick={() => {
                  void saveStep({
                    status: 'started',
                    stepName: 'explore_supercard',
                  }).then(
                    () => {
                      openExternalURL('/tms/super-card');
                      onClose();
                    },
                    () => undefined,
                  );
                }}
              />
            )}
          </Stack>
          {steps?.getting_started_guide?.status === 'pending' && (
            <Button
              onClick={() => {
                void saveStep({
                  stepName: 'getting_started_guide',
                  status: 'skipped',
                }).then(onClose);
              }}
              variant="text"
              pending={isPending}
            >
              Don&apos;t Show Again
            </Button>
          )}
        </Stack>
      </Column>
    </Columns>
  );
}

export function shouldShowGettingStartedIntro(
  verificationStatus:
    | CarrierSettings['carrier']['verification_status']
    | undefined,
): boolean {
  return (
    verificationStatus != null &&
    verificationStatus !== 'not_submitted' &&
    verificationStatus !== 'approved'
  );
}
